import { useEffect, useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { GRID_CLASSNAMES } from './constants';

import { FinanceFormValues, useFinanceFormContext } from '../../../context';

export type TermButtonValues = {
  term: number;
  label: string;
  key: string;
  id: string;
  onClick: () => void;
};

export interface TermsBuyNowPayLaterHook {
  deferredDurations: TermButtonValues[] | undefined;
  deferredGridClassName: string;
  showTerms: boolean;
  terms: TermButtonValues[] | undefined;
  termsGridClassName: string;
}

export default function useTermsBuyNowPayLater(): TermsBuyNowPayLaterHook {
  const { allRates } = useFinanceFormContext();
  const { setValue } = useFormContext<FinanceFormValues>();

  const [apr, aprType, duration, deferredDuration] = useWatch<
    FinanceFormValues,
    ['apr', 'aprType', 'duration', 'deferredDuration']
  >({
    name: ['apr', 'aprType', 'duration', 'deferredDuration'],
  });

  const key = useMemo(() => {
    if (!apr || !aprType) return undefined;
    return `${apr}-${aprType}`;
  }, [apr, aprType]);

  const showTerms = useMemo(
    () => Boolean(deferredDuration),
    [deferredDuration],
  );

  const deferredDurations = useMemo(() => {
    if (!key) return undefined;
    const aprRates = allRates?.[key];
    if (!aprRates) return undefined;
    return Object.keys(aprRates)
      .map((currentRate) => {
        const currentDeferred = currentRate.split('-')[0];
        return parseInt(currentDeferred, 10);
      })
      .filter((v, i, a) => a.indexOf(v) === i)
      .map((deferred) => ({
        term: deferred,
        label: `${deferred} months`,
        key: `${apr}-deferred-${deferred}-months`,
        id: `${apr}-deferred-${deferred}-months`,
        onClick: () => setValue('deferredDuration', deferred),
      }));
  }, [allRates, apr, key, setValue]);

  const deferredGridBaseClassName = useMemo(() => {
    if (!deferredDurations) return GRID_CLASSNAMES[1];
    if (deferredDurations.length > 3) return GRID_CLASSNAMES[4];
    return GRID_CLASSNAMES[deferredDurations.length];
  }, [deferredDurations]);

  const deferredGridClassName = useMemo(
    () =>
      ['gap-x-2 md:gap-x-3 gap-y-2 md:gap-y-3', deferredGridBaseClassName].join(
        ' ',
      ),
    [deferredGridBaseClassName],
  );

  const terms = useMemo(() => {
    if (!key || !deferredDuration) return undefined;
    const aprRates = allRates?.[key];
    if (!aprRates) return undefined;
    return Object.keys(aprRates)
      .filter((currentRate) => {
        const currentDeferred = currentRate.split('-')[0];
        return parseInt(currentDeferred, 10) === deferredDuration;
      })
      .map((currentTerm) => {
        const dur = currentTerm.split('-')[1];
        return {
          term: parseInt(dur, 10),
          label: `${dur} months`,
          key: `${apr}-repayment-${dur}-months`,
          id: `${apr}-repayment-${dur}-months`,
          onClick: () => setValue('duration', parseInt(dur, 10)),
        };
      });
  }, [allRates, deferredDuration, apr, key, setValue]);

  const termsGridBaseClassName = useMemo(() => {
    if (!terms) return GRID_CLASSNAMES[1];
    if (terms.length > 3) return GRID_CLASSNAMES[4];
    return GRID_CLASSNAMES[terms.length];
  }, [terms]);

  const termsGridClassName = useMemo(
    () =>
      ['gap-x-2 md:gap-x-3 gap-y-2 md:gap-y-3', termsGridBaseClassName].join(
        ' ',
      ),
    [termsGridBaseClassName],
  );

  const aprRef = useRef<string | undefined>();
  useEffect(() => {
    if (!deferredDurations) return;
    if (aprRef.current === apr) return;
    aprRef.current = apr;
    setValue('deferredDuration', deferredDurations[0].term);
  }, [apr, deferredDuration, deferredDurations, setValue]);

  const deferredRef = useRef<number | undefined>();
  useEffect(() => {
    if (!terms || !deferredDuration) return;
    if (deferredRef.current === deferredDuration) return;
    deferredRef.current = deferredDuration;
    setValue('duration', terms[0].term);
  }, [deferredDuration, duration, terms, setValue]);

  return {
    showTerms,
    deferredDurations,
    deferredGridClassName,
    terms,
    termsGridClassName,
  };
}
